<template>
    <LoadingComponent :props="loading" />
    <form @submit.prevent="save" class="w-full d-block">
        <div id="order_setup" class="db-card db-tab-div active" :class="{ 'on-update': onUpdateActive }">
            <div class="db-card-header items-center justify-between gap-4">
                <h3 class="db-card-title">{{ $t('menu.shipping_setup') }}</h3>
            
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="db-btn h-9 py-2 px-3 text-white bg-primary ml-3"
                    @click="refreshFromServer"
                    :disabled="isRefreshingView"
                    :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                >
                    <template v-if="isRefreshingView">
                        <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </template>
                </button>
            </div></div>
            <div class="db-card-body">
                <div class="form-row">
                    <div class="form-col-12">
                        <label class="db-field-title required" for="auto_update">{{ $t("label.select_shipping_method")
                        }}</label>
                        <div class="flex flex-wrap gap-6 pt-2">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input @click="onMethodClick(enums.shippingMethodEnum.PRODUCT_WISE, $event, 'tab-action', 'tab-content', 'productWise')"
                                        :value="enums.shippingMethodEnum.PRODUCT_WISE" v-model="form.shipping_setup_method"
                                        id="product_wise" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="product_wise" class="db-field-label flex items-center gap-2">
                                    {{ $t("label.product_wise")}}
                                </label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input @click="onMethodClick(enums.shippingMethodEnum.FLAT_RATE_WISE, $event, 'tab-action', 'tab-content', 'flatRate')"
                                        :value="enums.shippingMethodEnum.FLAT_RATE_WISE" v-model="form.shipping_setup_method"
                                        type="radio" id="flat_rate_wise" class="tab-action custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="flat_rate_wise" class="db-field-label flex items-center gap-2">
                                    {{ $t("label.flat_rate_wise") }}
                                </label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input @click="onMethodClick(enums.shippingMethodEnum.AREA_WISE, $event, 'tab-action', 'tab-content', 'areaWise')"
                                        :value="enums.shippingMethodEnum.AREA_WISE" v-model="form.shipping_setup_method"
                                        type="radio" id="area_wise" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="area_wise" class="db-field-label flex items-center gap-2">
                                    {{ $t("label.area_wise") }}
                                </label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input @click="onMethodClick(enums.shippingMethodEnum.SKYDROPX, $event, 'tab-action', 'tab-content', 'providerWise')"
                                        :value="enums.shippingMethodEnum.SKYDROPX" v-model="form.shipping_setup_method"
                                        type="radio" id="shipping_gateway" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="shipping_gateway" class="db-field-label flex items-center gap-2">
                                    {{ $t("label.provider_wise") }}
                                </label>
                            </div>
                            <!-- <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input @click="onMethodClick(enums.shippingMethodEnum.HYBRID, $event, 'tab-action', 'tab-content', 'hybridWise')"
                                        :value="enums.shippingMethodEnum.HYBRID" v-model="form.shipping_setup_method"
                                        type="radio" id="hybrid_wise" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="hybrid_wise" class="db-field-label flex items-center gap-2">
                                    {{ $t("label.hybrid_wise") }}
                                </label>
                            </div> -->
                        </div>
                        <small class="db-field-alert" v-if="errors.shipping_setup_method">
                            {{ errors.shipping_setup_method[0] }}
                        </small>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3"
                        v-if="form.shipping_setup_method === enums.shippingMethodEnum.PRODUCT_WISE">
                        <div class="flex items-center gap-3">
                            <label class="db-field-label flex items-center p-0">
                                <SwitchToggle
                                    class="scale-75 origin-left"
                                    :checked="defaultMethod === enums.shippingMethodEnum.PRODUCT_WISE"
                                    :disabled="!canSelectMethod(enums.shippingMethodEnum.PRODUCT_WISE)"
                                    @change="() => toggleDefault(enums.shippingMethodEnum.PRODUCT_WISE)" />
                                <span v-if="defaultMethod === enums.shippingMethodEnum.PRODUCT_WISE">{{ $t('label.method_default') }}</span>
                                <span v-else>{{ $t('label.make_default') }}</span>
                            </label>
                        </div>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3"
                        v-if="form.shipping_setup_method === enums.shippingMethodEnum.FLAT_RATE_WISE">
                        <div class="flex items-center justify-between gap-3 flex-wrap">
                            <div class="flex items-center gap-2">
                                <label class="db-field-label flex items-center p-0">
                                    <SwitchToggle
                                        class="scale-75 origin-left"
                                        :checked="defaultMethod === enums.shippingMethodEnum.FLAT_RATE_WISE"
                                        :disabled="!canSelectMethod(enums.shippingMethodEnum.FLAT_RATE_WISE)"
                                        @change="() => toggleDefault(enums.shippingMethodEnum.FLAT_RATE_WISE)" />
                                    <span v-if="defaultMethod === enums.shippingMethodEnum.FLAT_RATE_WISE">{{ $t('label.method_default') }}</span>
                                    <span v-else>{{ $t('label.make_default') }}</span>
                                </label>
                                <div v-if="!canSelectMethod(enums.shippingMethodEnum.FLAT_RATE_WISE)" class="flex items-center gap-3">
                                    <div class="border-l h-6 w-0"></div>
                                    <small class="leading-4">{{ $t('message.shipping_method_incomplete') }}</small>
                                </div>
                            </div>
                            <button type="button" class="db-btn h-10 bg-gray-100"
                                :class="isCheckingMethod(enums.shippingMethodEnum.FLAT_RATE_WISE) ? 'disabled:opacity-50 disabled:bg-gray' : ''"
                                :disabled="isCheckingMethod(enums.shippingMethodEnum.FLAT_RATE_WISE)"
                                @click="checkMethodConfiguration(enums.shippingMethodEnum.FLAT_RATE_WISE)">
                                <span v-if="isCheckingMethod(enums.shippingMethodEnum.FLAT_RATE_WISE)" class="flex items-center gap-2 text-white">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                    {{ $t('button.checking_status') }}
                                </span>
                                <span v-else class="flex items-center gap-2">
                                    <i class="fa-solid fa-rotate-right"></i>
                                    {{ $t('button.check_config_status') }}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3"
                        v-if="form.shipping_setup_method === enums.shippingMethodEnum.AREA_WISE">
                        <div class="flex items-center justify-between gap-3 flex-wrap">
                            <div class="flex items-center gap-2">
                                <label class="db-field-label flex items-center p-0">
                                    <SwitchToggle
                                        class="scale-75 origin-left"
                                        :checked="defaultMethod === enums.shippingMethodEnum.AREA_WISE"
                                        :disabled="!canSelectMethod(enums.shippingMethodEnum.AREA_WISE)"
                                        @change="() => toggleDefault(enums.shippingMethodEnum.AREA_WISE)" />
                                    <span v-if="defaultMethod === enums.shippingMethodEnum.AREA_WISE">{{ $t('label.method_default') }}</span>
                                    <span v-else>{{ $t('label.make_default') }}</span>
                                </label>
                                <div v-if="!canSelectMethod(enums.shippingMethodEnum.AREA_WISE)" class="flex items-center gap-3">
                                    <div class="border-l h-6 w-0"></div>
                                    <small class="leading-4">{{ $t('message.shipping_method_incomplete') }}</small>
                                </div>
                            </div>
                            <button type="button" class="db-btn h-10 bg-gray-100"
                                :class="isCheckingMethod(enums.shippingMethodEnum.AREA_WISE) ? 'disabled:opacity-50 disabled:bg-gray' : ''"
                                :disabled="isCheckingMethod(enums.shippingMethodEnum.AREA_WISE)"
                                @click="checkMethodConfiguration(enums.shippingMethodEnum.AREA_WISE)">
                                <span v-if="isCheckingMethod(enums.shippingMethodEnum.AREA_WISE)" class="flex items-center gap-2 text-white">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                    {{ $t('button.checking_status') }}
                                </span>
                                <span v-else class="flex items-center gap-2">
                                    <i class="fa-solid fa-rotate-right"></i>
                                    {{ $t('button.check_config_status') }}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3"
                        v-if="form.shipping_setup_method === enums.shippingMethodEnum.SKYDROPX">
                        <div class="flex items-center justify-between gap-3 flex-wrap">
                            <div class="flex items-center gap-2">
                                <label class="db-field-label flex items-center p-0">
                                    <SwitchToggle
                                        class="scale-75 origin-left"
                                        :checked="defaultMethod === enums.shippingMethodEnum.SKYDROPX"
                                        :disabled="!canSelectMethod(enums.shippingMethodEnum.SKYDROPX)"
                                        @change="() => toggleDefault(enums.shippingMethodEnum.SKYDROPX)" />
                                    <span v-if="defaultMethod === enums.shippingMethodEnum.SKYDROPX">{{ $t('label.method_default') }}</span>
                                    <span v-else>{{ $t('label.make_default') }}</span>
                                </label>
                                <div v-if="!canSelectMethod(enums.shippingMethodEnum.SKYDROPX)" class="flex items-center gap-3">
                                    <div class="border-l h-6 w-0"></div>
                                    <small class="leading-4">{{ $t('message.shipping_method_incomplete') }}</small>
                                </div>
                            </div>
                            <button type="button" class="db-btn h-10 bg-gray-100"
                                :class="isCheckingMethod(enums.shippingMethodEnum.SKYDROPX) ? 'disabled:opacity-50 disabled:bg-gray' : ''"
                                :disabled="isCheckingMethod(enums.shippingMethodEnum.SKYDROPX)"
                                @click="checkMethodConfiguration(enums.shippingMethodEnum.SKYDROPX)">
                                <span v-if="isCheckingMethod(enums.shippingMethodEnum.SKYDROPX)" class="flex items-center gap-2 text-white">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                    {{ $t('button.checking_status') }}
                                </span>
                                <span v-else class="flex items-center gap-2">
                                    <i class="fa-solid fa-rotate-right"></i>
                                    {{ $t('button.check_config_status') }}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3"
                        v-if="form.shipping_setup_method === enums.shippingMethodEnum.HYBRID">
                        <div class="flex items-center justify-between gap-3 flex-wrap">
                            <div class="flex items-center gap-2">
                                <label class="db-field-label flex items-center p-0">
                                    <SwitchToggle
                                        class="scale-75 origin-left"
                                        :checked="defaultMethod === enums.shippingMethodEnum.HYBRID"
                                        :disabled="!canSelectMethod(enums.shippingMethodEnum.HYBRID)"
                                        @change="() => toggleDefault(enums.shippingMethodEnum.HYBRID)" />
                                    <span v-if="defaultMethod === enums.shippingMethodEnum.HYBRID">{{ $t('label.method_default') }}</span>
                                    <span v-else>{{ $t('label.make_default') }}</span>
                                </label>
                                <div v-if="!canSelectMethod(enums.shippingMethodEnum.HYBRID)" class="flex items-center gap-3">
                                    <div class="border-l h-6 w-0"></div>
                                    <small class="leading-4">{{ $t('message.shipping_method_incomplete') }}</small>
                                </div>
                            </div>
                                <button type="button" class="db-btn h-10 bg-gray-100"
                                    :class="isCheckingMethod(enums.shippingMethodEnum.HYBRID) ? 'disabled:opacity-50 disabled:bg-gray' : ''"
                                    :disabled="isCheckingMethod(enums.shippingMethodEnum.HYBRID)"
                                    @click="checkMethodConfiguration(enums.shippingMethodEnum.HYBRID)">
                                    <span v-if="isCheckingMethod(enums.shippingMethodEnum.HYBRID)" class="flex items-center gap-2 text-white">
                                        <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                        </svg>
                                        {{ $t('button.checking_status') }}
                                    </span>
                                    <span v-else class="flex items-center gap-2">
                                        <i class="fa-solid fa-rotate-right"></i>
                                        {{ $t('button.check_config_status') }}
                                    </span>
                                </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="db-card tab-content mt-4" id="productWise" :class="{ 'active': form.shipping_setup_method === enums.shippingMethodEnum.PRODUCT_WISE, 'on-update': onUpdateActive }">
            <div class="db-card-header">
                <h3 class="db-card-title w-full flex justify-between items-center gap-2">
                    {{ $t('label.product_wise') }} 
                    <div class="text-xs flex justify-between items-center gap-2">
                        {{ $t('label.product_wise_tip') }} 
                    </div>
                </h3>
            </div>
            <div class="db-card-body">
                <div class="text-sm">{{ $t('label.product_wise_description') }}</div>
                <div class="text-xs">{{ $t('label.product_wise_notice') }}</div>
            </div>
        </div>

        <div class="db-card tab-content mt-4" id="flatRate"
            :class="{ 'active': form.shipping_setup_method === enums.shippingMethodEnum.FLAT_RATE_WISE, 'on-update': onUpdateActive }">
            <div class="db-card-header">
                <h3 class="db-card-title w-full flex justify-between items-center gap-2">
                    {{ $t('label.shipping_flat_rate') }} 
                    <div v-if="getMethodState(enums.shippingMethodEnum.FLAT_RATE_WISE)" class="text-xs flex justify-between items-center gap-2">
                        {{ $t('label.config_state') }} 
                        <span class="d-blok w-4 h-4 rounded-full ml-2" :class="getMethodStateClass(enums.shippingMethodEnum.FLAT_RATE_WISE)"></span>
                    </div>
                </h3>
            </div>
            <div class="db-card-body">
                <div class="form-row">
                    <div class="form-col-12 sm:form-col-6">
                        <label for="name" class="db-field-title required">
                            {{ $t("label.shipping_cost") }}
                        </label>
                        <input v-on:keypress="floatNumber($event)" v-model="form.shipping_setup_flat_rate_wise_cost"
                            v-bind:class="errors.shipping_setup_flat_rate_wise_cost ? 'invalid' : ''" type="text" id="name"
                            class="db-field-control" />
                        <small class="db-field-alert" v-if="errors.shipping_setup_flat_rate_wise_cost">{{
                            errors.shipping_setup_flat_rate_wise_cost[0]
                        }}</small>
                    </div>
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.is_product_quantity_multiply') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="flatMultiplyYes" class="custom-radio-field"
                                        :value="true" v-model="form.shipping_setup_flat_rate_multiply_per_item">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="flatMultiplyYes" class="db-field-label">{{ $t('label.yes') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="flatMultiplyNo" class="custom-radio-field"
                                        :value="false" v-model="form.shipping_setup_flat_rate_multiply_per_item">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="flatMultiplyNo" class="db-field-label">{{ $t('label.no') }}</label>
                            </div>
                        </div>
                    </div>
                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="db-card tab-content mt-4" id="areaWise"
            :class="{ 'active': form.shipping_setup_method === enums.shippingMethodEnum.AREA_WISE, 'on-update': onUpdateActive }">
            <div class="db-card-header">
                <h3 class="db-card-title w-full flex justify-between items-center gap-2">
                    {{ $t('label.shipping_area') }} 
                    <div v-if="getMethodState(enums.shippingMethodEnum.AREA_WISE)" class="text-xs flex justify-between items-center gap-2">
                        {{ $t('label.config_state') }} 
                        <span class="d-blok w-4 h-4 rounded-full ml-2" :class="getMethodStateClass(enums.shippingMethodEnum.AREA_WISE)"></span>
                    </div>
                </h3>
            </div>
            <div class="db-card-body">
                <div class="form-row">
                    <div class="form-col-12 sm:form-col-6">
                        <label for="name" class="db-field-title required">
                            {{ $t("label.default_shipping_cost") }}
                        </label>
                        <input v-on:keypress="floatNumber($event)" v-model="form.shipping_setup_area_wise_default_cost"
                            v-bind:class="errors.shipping_setup_area_wise_default_cost ? 'invalid' : ''" type="text"
                            id="name" class="db-field-control" />
                        <small class="db-field-alert" v-if="errors.shipping_setup_area_wise_default_cost">{{
                            errors.shipping_setup_area_wise_default_cost[0]
                        }}</small>
                    </div>
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.is_product_quantity_multiply') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="areaMultiplyYes" class="custom-radio-field"
                                        :value="true" v-model="form.shipping_setup_area_wise_multiply_per_item">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="areaMultiplyYes" class="db-field-label">{{ $t('label.yes') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="areaMultiplyNo" class="custom-radio-field"
                                        :value="false" v-model="form.shipping_setup_area_wise_multiply_per_item">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="areaMultiplyNo" class="db-field-label">{{ $t('label.no') }}</label>
                            </div>
                        </div>
                    </div>
                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="db-card tab-content mt-4" id="providerWise"
            :class="{ 'active': form.shipping_setup_method === enums.shippingMethodEnum.SKYDROPX, 'on-update': onUpdateActive }">
            <div class="db-card-header">
                <h3 class="db-card-title w-full flex justify-between items-center gap-2">
                    {{ $t('label.shipping_gateway') }} 
                    <div v-if="getMethodState(enums.shippingMethodEnum.SKYDROPX)" class="text-xs flex justify-between items-center gap-2">
                        {{ $t('label.config_state') }} 
                        <span class="d-blok w-4 h-4 rounded-full ml-2" :class="getMethodStateClass(enums.shippingMethodEnum.SKYDROPX)"></span>
                    </div>
                </h3>
            </div>
            <div class="db-card-body">
                <div class="form-row">
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.status') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="providerEnabled" class="custom-radio-field"
                                        :value="true" v-model="form.shipping_setup_provider_enabled">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="providerEnabled" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="providerDisabled" class="custom-radio-field"
                                        :value="false" v-model="form.shipping_setup_provider_enabled">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="providerDisabled" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                        <div class="text-xs mt-2">{{ $t('label.provider_wise_description') }}</div>
                    </div>
                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="db-card tab-content mt-4" id="hybridWise"
            :class="{ 'active': form.shipping_setup_method === enums.shippingMethodEnum.HYBRID, 'on-update': onUpdateActive }">
            <div class="db-card-header">
                <h3 class="db-card-title w-full flex justify-between items-center gap-2">
                    {{ $t('label.hybrid_product_provider') }} 
                    <div v-if="getMethodState(enums.shippingMethodEnum.HYBRID)" class="text-xs flex justify-between items-center gap-2">
                        {{ $t('label.config_state') }} 
                        <span class="d-blok w-4 h-4 rounded-full ml-2" :class="getMethodStateClass(enums.shippingMethodEnum.HYBRID)"></span>
                    </div>
                </h3>
            </div>
            <div class="db-card-body">
                <div class="form-row">
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.provider_wise') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="hybridProviderEnabled" class="custom-radio-field"
                                        :value="true" v-model="form.shipping_setup_provider_enabled">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="hybridProviderEnabled" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="hybridProviderDisabled" class="custom-radio-field"
                                        :value="false" v-model="form.shipping_setup_provider_enabled">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="hybridProviderDisabled" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                        <div class="text-xs mt-2">{{ $t('label.hybrid_provider_wise_description') }}</div>
                    </div>
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.product_wise') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="hybridProductEnabled" class="custom-radio-field"
                                        :value="true" v-model="form.shipping_setup_product_config_enabled">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="hybridProductEnabled" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="hybridProductDisabled" class="custom-radio-field"
                                        :value="false" v-model="form.shipping_setup_product_config_enabled">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="hybridProductDisabled" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                        <div class="text-xs mt-2">{{ $t('label.hybrid_product_wise_description') }}</div>
                    </div>
                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </form>
    
    <div class="db-card tab-content mt-4" id="areaWise"
        :class="form.shipping_setup_method === enums.shippingMethodEnum.AREA_WISE ? 'active' : ''">
        <OrderAreaListComponent />
    </div>

    <!-- General Settings Card (Applies to all shipping methods) -->
    <form @submit.prevent="saveGeneral" class="w-full d-block mt-4">
        <div class="db-card" :class="{ 'on-update': onGeneralUpdateActive }">
            <div class="db-card-header">
                <h3 class="db-card-title">{{ $t('label.general_settings') }}</h3>
            </div>
            <div class="db-card-body">
                <div class="form-row">
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.enable_billing_address') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="billingAddressEnabled" class="custom-radio-field"
                                        :value="true" v-model="form.shipping_setup_enable_billing_address">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="billingAddressEnabled" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input type="radio" id="billingAddressDisabled" class="custom-radio-field"
                                        :value="false" v-model="form.shipping_setup_enable_billing_address">
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="billingAddressDisabled" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- Divider -->
                    <hr class="w-full border-gray-100 my-6" />

                    <!-- Default Product Shipping & Return Fallback Text -->
                    <div class="form-col-12">
                        <label for="product_shipping_and_return_default_text" class="db-field-title">
                            {{ $t('label.product_shipping_and_return_default_text') }}
                        </label>
                        <textarea
                            v-model="form.shipping_setup_product_shipping_and_return_default_text"
                            id="product_shipping_and_return_default_text"
                            class="db-field-control !h-24"
                        ></textarea>
                        <small class="db-field-alert" v-if="errors.shipping_setup_product_shipping_and_return_default_text">
                            {{ errors.shipping_setup_product_shipping_and_return_default_text[0] }}
                        </small>
                    </div>

                    <!-- Divider -->
                    <hr class="w-full border-gray-100 my-6" />

                    <!-- Free Shipping Window Enabled Switch -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.enable_free_shipping_window') }}</label>
                        <div class="flex items-center gap-3 border h-10 rounded-md">
                            <div class="flex items-center justify-center py-2 px-3 border-r">
                                <div class="custom-switch">
                                    <input
                                        type="checkbox"
                                        :checked="!!form.shipping_setup_free_shipping_window_enabled"
                                        @change="form.shipping_setup_free_shipping_window_enabled = $event.target.checked"
                                    >
                                </div>
                            </div>
                            <small class="text-xs">{{ $t('message.free_shipping_window_hint') }}</small>
                        </div>
                    </div>

                    <!-- Window Type Select -->
                    <div class="form-col-12 sm:form-col-6" v-if="form.shipping_setup_free_shipping_window_enabled">
                        <label class="db-field-title required">{{ $t('label.free_shipping_window_type') }}</label>
                        <vue-select
                            class="db-field-control f-b-custom-select"
                            v-model="form.shipping_setup_free_shipping_window_type"
                            :options="freeShippingWindowTypeOptions"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                        />
                    </div>

                    <!-- Start and End Time (for Por Horario Específico) -->
                    <div class="form-col-12 sm:form-col-6" v-if="form.shipping_setup_free_shipping_window_enabled && form.shipping_setup_free_shipping_window_type === 'specific_schedule'">
                        <label class="db-field-title required">{{ $t('label.free_shipping_window_start_time') }}</label>
                        <input
                            type="time"
                            v-model="form.shipping_setup_free_shipping_window_start_time"
                            class="db-field-control"
                        />
                    </div>
                    <div class="form-col-12 sm:form-col-6" v-if="form.shipping_setup_free_shipping_window_enabled && form.shipping_setup_free_shipping_window_type === 'specific_schedule'">
                        <label class="db-field-title required">{{ $t('label.free_shipping_window_end_time') }}</label>
                        <input
                            type="time"
                            v-model="form.shipping_setup_free_shipping_window_end_time"
                            class="db-field-control"
                        />
                    </div>

                    <!-- Hours After (for Rango de Tiempo) -->
                    <div class="form-col-12 sm:form-col-6" v-if="form.shipping_setup_free_shipping_window_enabled && form.shipping_setup_free_shipping_window_type === 'time_range'">
                        <label class="db-field-title required">{{ $t('label.free_shipping_window_hours_after') }}</label>
                        <input
                            type="number"
                            min="1"
                            v-model.number="form.shipping_setup_free_shipping_window_hours_after"
                            class="db-field-control"
                        />
                    </div>

                    <!-- Horario Máximo Time Input -->
                    <div class="form-col-12 sm:form-col-6" v-if="form.shipping_setup_free_shipping_window_enabled">
                        <label class="db-field-title">{{ $t('label.free_shipping_window_max_hour') }}</label>
                        <input
                            type="time"
                            v-model="form.shipping_setup_free_shipping_window_max_hour"
                            class="db-field-control"
                        />
                        <small class="text-xs mt-2 block">{{ $t('message.free_shipping_window_max_hour_hint') }}</small>
                    </div>

                    <!-- Free Shipping Default Text -->
                    <div class="form-col-12" v-if="form.shipping_setup_free_shipping_window_enabled">
                        <label for="free_shipping_default_text" class="db-field-title">
                            {{ $t('label.free_shipping_default_text') }}
                        </label>
                        <textarea
                            v-model="form.shipping_setup_free_shipping_default_text"
                            id="free_shipping_default_text"
                            class="db-field-control !h-24"
                        ></textarea>
                        <small class="db-field-alert" v-if="errors.shipping_setup_free_shipping_default_text">
                            {{ errors.shipping_setup_free_shipping_default_text[0] }}
                        </small>
                    </div>

                    <!-- Normal Shipping Default Text -->
                    <div class="form-col-12">
                        <label for="normal_shipping_default_text" class="db-field-title">
                            {{ $t('label.normal_shipping_default_text') }}
                        </label>
                        <textarea
                            v-model="form.shipping_setup_normal_shipping_default_text"
                            id="normal_shipping_default_text"
                            class="db-field-control !h-24"
                        ></textarea>
                        <small class="db-field-alert" v-if="errors.shipping_setup_normal_shipping_default_text">
                            {{ errors.shipping_setup_normal_shipping_default_text[0] }}
                        </small>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isGeneralSaving">
                            <template v-if="isGeneralSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import shippingMethodEnum from "../../../../enums/modules/shippingMethodEnum";
import LoadingComponent from "../../components/LoadingComponent";
import alertService from "../../../../services/alertService";
import targetService from "../../../../services/targetService";
import appService from "../../../../services/appService";
import OrderAreaListComponent from "./OrderArea/OrderAreaListComponent";
import SwitchToggle from "../../components/SwitchToggle";

export default {
    name: "ShippingSetupComponent",
    components: { LoadingComponent, OrderAreaListComponent, SwitchToggle },
    data() {
        return {
            isRefreshingView: false,
            loading: {
                isActive: false
            },
            form: {
                shipping_setup_method: null,
                shipping_setup_flat_rate_wise_cost: "",
                shipping_setup_area_wise_default_cost: "",
                shipping_setup_flat_rate_multiply_per_item: false,
                shipping_setup_area_wise_multiply_per_item: false,
                shipping_setup_provider_enabled: false,
                shipping_setup_product_config_enabled: true,
                shipping_setup_enable_billing_address: true,
                shipping_setup_free_shipping_window_enabled: false,
                shipping_setup_free_shipping_window_type: "current_day",
                shipping_setup_free_shipping_window_max_hour: "",
                shipping_setup_free_shipping_window_start_time: "",
                shipping_setup_free_shipping_window_end_time: "",
                shipping_setup_free_shipping_window_hours_after: "",
                shipping_setup_free_shipping_default_text: "",
                shipping_setup_normal_shipping_default_text: "",
                shipping_setup_product_shipping_and_return_default_text: "",
            },
            freeShippingWindowTypeOptions: [
                { id: 'current_day', name: this.$t('label.free_shipping_window_type_current_day') },
                { id: 'next_24_hours', name: this.$t('label.free_shipping_window_type_next_24_hours') },
                { id: 'specific_schedule', name: this.$t('label.free_shipping_window_type_specific_schedule') },
                { id: 'time_range', name: this.$t('label.free_shipping_window_type_time_range') }
            ],
            enums: {
                shippingMethodEnum: shippingMethodEnum
            },
            defaultMethod: null,
            providerHealth: {
                state: 'red',
            },
            checkingMethodState: false,
            checkingMethod: null,
            savingDefault: false,
            isSaving: false,
            onUpdateActive: false,
            isGeneralSaving: false,
            onGeneralUpdateActive: false,
            errors: {}
        }
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        fetchData: function (showOverlay = true) {
            if (showOverlay) {
                this.loading.isActive = true;
            }
            const p1 = this.$store.dispatch('orderArea/lists', { vuex: true });
            const p2 = this.$store.dispatch('shippingSetup/lists');
            return Promise.all([p1, p2]).then(([areaRes, res]) => {
                this.providerHealth = res.data.data.shipping_setup_provider_health || this.providerHealth;
                this.defaultMethod = res.data.data.shipping_setup_method;
                this.form = {
                    shipping_setup_method: res.data.data.shipping_setup_method,
                    shipping_setup_flat_rate_wise_cost: res.data.data.shipping_setup_flat_rate_wise_cost,
                    shipping_setup_area_wise_default_cost: res.data.data.shipping_setup_area_wise_default_cost,
                    shipping_setup_flat_rate_multiply_per_item: !!res.data.data.shipping_setup_flat_rate_multiply_per_item,
                    shipping_setup_area_wise_multiply_per_item: !!res.data.data.shipping_setup_area_wise_multiply_per_item,
                    shipping_setup_provider_enabled: !!res.data.data.shipping_setup_provider_enabled,
                    shipping_setup_product_config_enabled: !!res.data.data.shipping_setup_product_config_enabled,
                    shipping_setup_enable_billing_address: typeof res.data.data.shipping_setup_enable_billing_address !== 'undefined' ? !!res.data.data.shipping_setup_enable_billing_address : true,
                    shipping_setup_free_shipping_window_enabled: typeof res.data.data.shipping_setup_free_shipping_window_enabled !== 'undefined' ? !!res.data.data.shipping_setup_free_shipping_window_enabled : false,
                    shipping_setup_free_shipping_window_type: res.data.data.shipping_setup_free_shipping_window_type || "current_day",
                    shipping_setup_free_shipping_window_max_hour: res.data.data.shipping_setup_free_shipping_window_max_hour || "",
                    shipping_setup_free_shipping_window_start_time: res.data.data.shipping_setup_free_shipping_window_start_time || "",
                    shipping_setup_free_shipping_window_end_time: res.data.data.shipping_setup_free_shipping_window_end_time || "",
                    shipping_setup_free_shipping_window_hours_after: res.data.data.shipping_setup_free_shipping_window_hours_after || "",
                    shipping_setup_free_shipping_default_text: res.data.data.shipping_setup_free_shipping_default_text || "",
                    shipping_setup_normal_shipping_default_text: res.data.data.shipping_setup_normal_shipping_default_text || "",
                    shipping_setup_product_shipping_and_return_default_text: res.data.data.shipping_setup_product_shipping_and_return_default_text || "",
                };
                if (showOverlay) {
                    this.loading.isActive = false;
                }
            }).catch((err) => {
                if (showOverlay) {
                    this.loading.isActive = false;
                }
            });
        },
        refreshFromServer: function () {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.onUpdateActive = true;
            this.fetchData(false).then(() => {
                alertService.info(this.$t('button.refresh'));
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            }).catch(() => {
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            });
        },
        multiTargets: function (event, commonBtnClass, commonDivClass, targetID) {
            targetService.multiTargets(event, commonBtnClass, commonDivClass, targetID);
        },
        floatNumber(e) {
            return appService.floatNumber(e);
        },
        canSelectMethod(method) {
            if (parseInt(method) === parseInt(this.enums.shippingMethodEnum.PRODUCT_WISE)) {
                return true;
            }
            const state = this.getMethodState(method);
            return state === 'green';
        },
        onMethodClick(method, event, commonBtnClass, commonDivClass, targetID) {
            try {
                this.form.shipping_setup_method = method;
                targetService.multiTargets(event, commonBtnClass, commonDivClass, targetID);
            } catch (e) {
                // ignore
            }
        },
        toggleDefault(method) {
            try {
                if (!this.canSelectMethod(method)) {
                    return;
                }
                this.form.shipping_setup_method = method;
                this.defaultMethod = method;
                this.savingDefault = true;
                this.save();
            } catch (e) {
                // ignore
            }
        },
        isCheckingMethod(method) {
            return this.checkingMethodState && parseInt(this.checkingMethod) === parseInt(method);
        },
        checkMethodConfiguration: function (method) {
            if (this.checkingMethodState) {
                return;
            }

            try {
                this.checkingMethodState = true;
                this.checkingMethod = method;

                const isAreaWise = parseInt(method) === parseInt(this.enums.shippingMethodEnum.AREA_WISE);
                const areaRefreshPromise = isAreaWise
                    ? this.$store.dispatch('orderArea/lists', { vuex: true })
                    : Promise.resolve();

                areaRefreshPromise.then(() => {
                    return this.$store.dispatch('shippingSetup/lists');
                }).then((res) => {
                    this.providerHealth = res.data.data.shipping_setup_provider_health || this.providerHealth;

                    if (res.data && res.data.data && typeof res.data.data.shipping_setup_method !== 'undefined') {
                        this.defaultMethod = res.data.data.shipping_setup_method;
                    }
                }).catch((err) => {
                    alertService.error(err);
                }).finally(() => {
                    this.checkingMethodState = false;
                    this.checkingMethod = null;
                });
            } catch (err) {
                this.checkingMethodState = false;
                this.checkingMethod = null;
                alertService.error(err);
            }
        },
        getMethodStateClass(method) {
            const state = this.getMethodState(method);
            if (state === 'green') {
                return 'bg-success';
            }

            if (state === 'yellow') {
                return 'bg-warning';
            }

            return 'bg-danger';
        },
        getMethodState(method) {
            const isSelected = parseInt(this.form.shipping_setup_method) === parseInt(method);
            const hasFlatCost = this.form.shipping_setup_flat_rate_wise_cost !== '' && parseFloat(this.form.shipping_setup_flat_rate_wise_cost) >= 0;
            const hasAreaCost = this.form.shipping_setup_area_wise_default_cost !== '' && parseFloat(this.form.shipping_setup_area_wise_default_cost) >= 0;
            const hasAreaMultiply = typeof this.form.shipping_setup_area_wise_multiply_per_item !== 'undefined';
            const orderAreas = this.$store && this.$store.getters && this.$store.getters["orderArea/lists"] ? this.$store.getters["orderArea/lists"] : [];
            const hasAreas = Array.isArray(orderAreas) && orderAreas.length > 0;
            const providerState = this.providerHealth?.state || 'red';

            if (method === this.enums.shippingMethodEnum.PRODUCT_WISE) {
                return isSelected ? 'green' : 'red';
            }

            if (method === this.enums.shippingMethodEnum.FLAT_RATE_WISE) {
                if (hasFlatCost) {
                    return 'green';
                }
                return isSelected ? 'yellow' : 'red';
            }

            if (method === this.enums.shippingMethodEnum.AREA_WISE) {
                if (hasAreaCost && hasAreaMultiply && hasAreas) {
                    return 'green';
                }
                return isSelected ? 'yellow' : 'red';
            }

            if (method === this.enums.shippingMethodEnum.SKYDROPX) {
                if (providerState === 'green') {
                    return 'green';
                }

                if (providerState === 'yellow') {
                    return 'yellow';
                }

                return isSelected ? 'red' : 'red';
            }

            if (method === this.enums.shippingMethodEnum.HYBRID) {
                if (providerState === 'green' && this.form.shipping_setup_product_config_enabled) {
                    return 'green';
                }

                if (providerState === 'red') {
                    return 'red';
                }

                return isSelected ? 'yellow' : 'red';
            }

            return 'red';
        },
        save: function () {
            try {
                this.isSaving = true;
                this.onUpdateActive = true;
                this.$store.dispatch("shippingSetup/save", this.form).then((res) => {
                    alertService.successFlip(res.config.method === "put" ?? 0, this.$t("menu.shipping_setup"));
                    this.providerHealth = res.data.data.shipping_setup_provider_health || this.providerHealth;
                    if (this.savingDefault) {
                        if (res.data && res.data.data && typeof res.data.data.shipping_setup_method !== 'undefined') {
                            this.defaultMethod = res.data.data.shipping_setup_method;
                        }
                    }
                    this.savingDefault = false;
                    this.form = {
                        shipping_setup_method: res.data.data.shipping_setup_method,
                        shipping_setup_flat_rate_wise_cost: res.data.data.shipping_setup_flat_rate_wise_cost,
                        shipping_setup_area_wise_default_cost: res.data.data.shipping_setup_area_wise_default_cost,
                        shipping_setup_flat_rate_multiply_per_item: !!res.data.data.shipping_setup_flat_rate_multiply_per_item,
                        shipping_setup_area_wise_multiply_per_item: !!res.data.data.shipping_setup_area_wise_multiply_per_item,
                        shipping_setup_provider_enabled: !!res.data.data.shipping_setup_provider_enabled,
                        shipping_setup_product_config_enabled: !!res.data.data.shipping_setup_product_config_enabled,
                        shipping_setup_enable_billing_address: typeof res.data.data.shipping_setup_enable_billing_address !== 'undefined' ? !!res.data.data.shipping_setup_enable_billing_address : true,
                        shipping_setup_free_shipping_window_enabled: typeof res.data.data.shipping_setup_free_shipping_window_enabled !== 'undefined' ? !!res.data.data.shipping_setup_free_shipping_window_enabled : false,
                        shipping_setup_free_shipping_window_type: res.data.data.shipping_setup_free_shipping_window_type || "current_day",
                        shipping_setup_free_shipping_window_max_hour: res.data.data.shipping_setup_free_shipping_window_max_hour || "",
                        shipping_setup_free_shipping_window_start_time: res.data.data.shipping_setup_free_shipping_window_start_time || "",
                        shipping_setup_free_shipping_window_end_time: res.data.data.shipping_setup_free_shipping_window_end_time || "",
                        shipping_setup_free_shipping_window_hours_after: res.data.data.shipping_setup_free_shipping_window_hours_after || "",
                        shipping_setup_free_shipping_default_text: res.data.data.shipping_setup_free_shipping_default_text || "",
                        shipping_setup_normal_shipping_default_text: res.data.data.shipping_setup_normal_shipping_default_text || "",
                        shipping_setup_product_shipping_and_return_default_text: res.data.data.shipping_setup_product_shipping_and_return_default_text || "",
                    };
                    this.errors = {};
                    this.isSaving = false;
                    this.onUpdateActive = false;
                }).catch((err) => {
                    this.errors = err.response.data.errors;
                    this.savingDefault = false;
                    this.isSaving = false;
                    this.onUpdateActive = false;
                });
            } catch (err) {
                alertService.error(err);
                this.savingDefault = false;
                this.isSaving = false;
                this.onUpdateActive = false;
            }
        },
        saveGeneral: function () {
            try {
                this.isGeneralSaving = true;
                this.onGeneralUpdateActive = true;
                this.$store.dispatch("shippingSetup/save", this.form).then((res) => {
                    alertService.successFlip(res.config.method === "put" ?? 0, this.$t("menu.shipping_setup"));
                    this.form = {
                        shipping_setup_method: res.data.data.shipping_setup_method,
                        shipping_setup_flat_rate_wise_cost: res.data.data.shipping_setup_flat_rate_wise_cost,
                        shipping_setup_area_wise_default_cost: res.data.data.shipping_setup_area_wise_default_cost,
                        shipping_setup_flat_rate_multiply_per_item: !!res.data.data.shipping_setup_flat_rate_multiply_per_item,
                        shipping_setup_area_wise_multiply_per_item: !!res.data.data.shipping_setup_area_wise_multiply_per_item,
                        shipping_setup_provider_enabled: !!res.data.data.shipping_setup_provider_enabled,
                        shipping_setup_product_config_enabled: !!res.data.data.shipping_setup_product_config_enabled,
                        shipping_setup_enable_billing_address: typeof res.data.data.shipping_setup_enable_billing_address !== 'undefined' ? !!res.data.data.shipping_setup_enable_billing_address : true,
                        shipping_setup_free_shipping_window_enabled: typeof res.data.data.shipping_setup_free_shipping_window_enabled !== 'undefined' ? !!res.data.data.shipping_setup_free_shipping_window_enabled : false,
                        shipping_setup_free_shipping_window_type: res.data.data.shipping_setup_free_shipping_window_type || "current_day",
                        shipping_setup_free_shipping_window_max_hour: res.data.data.shipping_setup_free_shipping_window_max_hour || "",
                        shipping_setup_free_shipping_window_start_time: res.data.data.shipping_setup_free_shipping_window_start_time || "",
                        shipping_setup_free_shipping_window_end_time: res.data.data.shipping_setup_free_shipping_window_end_time || "",
                        shipping_setup_free_shipping_window_hours_after: res.data.data.shipping_setup_free_shipping_window_hours_after || "",
                        shipping_setup_free_shipping_default_text: res.data.data.shipping_setup_free_shipping_default_text || "",
                        shipping_setup_normal_shipping_default_text: res.data.data.shipping_setup_normal_shipping_default_text || "",
                        shipping_setup_product_shipping_and_return_default_text: res.data.data.shipping_setup_product_shipping_and_return_default_text || "",
                    };
                    this.errors = {};
                    this.isGeneralSaving = false;
                    this.onGeneralUpdateActive = false;
                }).catch((err) => {
                    this.errors = err.response.data.errors;
                    this.isGeneralSaving = false;
                    this.onGeneralUpdateActive = false;
                });
            } catch (err) {
                alertService.error(err);
                this.isGeneralSaving = false;
                this.onGeneralUpdateActive = false;
            }
        },
    }
}
</script>